import { z } from "zod";

// Live video (issue #216, ADR-0018): a drone controller or another camera sends RTMP to the
// server, HQ watches it in the panel and in the app, as HLS a few seconds behind. Sources belong
// to the organization, so the pilot sets the address in the controller once, not per action.
// Nothing is recorded.

const Id = z.uuid();

export const VideoSourceDto = z.object({
  id: Id,
  name: z.string(),
  /** Something is being sent right now. */
  live: z.boolean(),
  /** HLS playlist, relative to the server origin (the panel's or the phone's `api` minus /api). */
  hlsPath: z.string(),
  /**
   * WHEP endpoint for low-latency WebRTC (#288), relative to the server origin. Both the web panel
   * and the phone try this first and fall back to `hlsPath` when WebRTC cannot connect (#319).
   */
  whepPath: z.string(),
  /**
   * An AI source's clean picture with the detections inside each frame (SEI, #318, ADR-0024), for
   * the HQ panel only, which draws them itself; null while the detector does not send it, and
   * always on phones and share links, which play the picture with the boxes burned in.
   */
  seiWhepPath: z.string().nullable(),
  /**
   * What the pilot types into the controller, `rtmp://<host>/live/<12 letters>` (the owner,
   * 2026-10-11: nothing can be pasted on a DJI controller). For HQ staff only; null on phones.
   */
  publishUrl: z.string().nullable(),
  /**
   * When this source is the AI-detection copy of a camera (#271), the id of that camera; null for a
   * normal camera. The panel labels and groups the AI source and shows the detection disclaimer.
   */
  aiOf: Id.nullable(),
});
export type VideoSourceDto = z.infer<typeof VideoSourceDto>;

/** Panel: the sources and a short-lived token the player sends as `Authorization: Bearer`. */
export const VideoList = z.object({
  sources: z.array(VideoSourceDto),
  viewToken: z.string(),
});
export type VideoList = z.infer<typeof VideoList>;

export const CreateVideoSourceInput = z.object({ name: z.string().trim().min(1).max(60) });
export type CreateVideoSourceInput = z.infer<typeof CreateVideoSourceInput>;

/** HQ in the app and commanders always watch; other participants when HQ lets them. */
export function mayWatchVideo(participant: { role: string; canViewVideo: boolean }): boolean {
  return participant.role === "hq" || participant.role === "commander" || participant.canViewVideo;
}

/** The media path is the stream key: `live/<key>` (ADR-0018). */
export const videoHlsPath = (streamKey: string) => `/live/${streamKey}/index.m3u8`;

/** WHEP endpoint for the same stream, for low-latency WebRTC in the panel (#288). */
export const videoWhepPath = (streamKey: string) => `/live/${streamKey}/whep`;

/** The AI source's clean+SEI variant sits next to its burned picture: `live/<key>/sei` (#318). */
export const videoSeiPath = (streamKey: string) => `live/${streamKey}/sei`;
export const videoSeiWhepPath = (streamKey: string) => `/${videoSeiPath(streamKey)}/whep`;

/**
 * The source to show: the one chosen while it exists, else the first live one, else the first.
 * A drone that starts sending appears by itself when nothing was chosen (panel and app).
 */
export function pickVideoSource(
  sources: readonly VideoSourceDto[],
  chosen: string | null,
): VideoSourceDto | null {
  return sources.find((s) => s.id === chosen) ?? sources.find((s) => s.live) ?? sources[0] ?? null;
}
